import { useInventoryCurrencyConverter } from "@/hooks/useInventoryCurrencyConverter";
import { useInfiniteActivitiesQuery } from "@/actions/activity.actions";
import { useGetCitiesQuery } from "@/actions/generics.actions";
import { useListSuppliersQuery } from "@/actions/suppliers.action";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ActivityType } from "@/interfaces";
import { activityCategories } from "@/lib/constants";
import { usePermissions } from "@/lib/permissions";
import { cx } from "@/lib/utils";
import EditActivityRepositoryPage from "@/pages/OrgPage/ActivitiesRepository/EditActivityRepositoryPage";
import { App as AntApp, Input, InputNumber, Modal, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { CarFront, Check, Grid2X2, LayoutGrid, List, MapPin, Search, TentTreeIcon } from "lucide-react";
import { useEffect, useState } from "react";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const GROUP_ACTIVITY_CATEGORIES = new Set(["Transfers", "Private Transfers", "Private Tours"]);

interface ModalAddActivitiesProps {
  open: boolean;
  destinations: string[];
  initialCountry?: string;
  initialCity?: string;
  onLocationChange?: (country: string, city?: string) => void;
  day: number;
  activities: ActivityType[];
  startDate?: Date;
  currency?: string;
  onAdd: (activities: ActivityType[]) => void;
  handleClose: () => void;
}

/**
 * Presents searchable activity inventory and returns the selected items with their applicable itinerary-day rates.
 */
export const ModalAddActivities = ({
  open,
  destinations,
  initialCountry,
  initialCity,
  onLocationChange,
  day,
  activities,
  startDate,
  currency,
  onAdd,
  handleClose,
}: ModalAddActivitiesProps) => {
  const { canAccessOrg } = usePermissions();
  const [selectedActivities, setSelectedActivities] = useState<ActivityType[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  const toggleSelectedActivity = (Activity: ActivityType) => {
    if (selectedActivities.map((a) => a.id).includes(Activity.id)) {
      setSelectedActivities(selectedActivities.filter((a) => a.id !== Activity.id));
    } else {
      setSelectedActivities([...selectedActivities, Activity]);
    }
  };

  const handleOk = async () => {
    const currentDate = startDate ? dayjs(startDate).add(day, "day") : null;
    // Rates held only in another currency are converted first so the lookups below find a cost in the itinerary currency.
    const convertedActivities = await convertRates("activity", selectedActivities);
    const activitiesToAdd = convertedActivities.map((originalActivity) => {
      const Activity = { ...originalActivity };
      if (!currency) {
        Activity.quantity = 1;
        return Activity;
      }
      let seasonalCost;

      if (currentDate) {
        seasonalCost = Activity?.seasonal?.find((seasonal) => {
          if (seasonal.daterange && seasonal.daterange.length === 2) {
            const [start, end] = seasonal.daterange;
            return currentDate.isSameOrAfter(dayjs(start)) && currentDate.isSameOrBefore(dayjs(end));
          }
          return false;
        });
      }

      if (seasonalCost) {
        if (GROUP_ACTIVITY_CATEGORIES.has(Activity.category)) {
          Activity.costForAdult = seasonalCost.tariff.find((t) => t.currency === currency)?.costForAdult || 0;
        } else {
          Activity.costForAdult =
            seasonalCost.tariff.find((t) => t.currency === currency)?.costForAdult ||
            Activity.tariff?.find((t) => t.currency === currency)?.costForAdult ||
            0;
          Activity.costForCWB =
            seasonalCost.tariff.find((t) => t.currency === currency)?.costForCWB ||
            Activity.tariff?.find((t) => t.currency === currency)?.costForCWB ||
            0;
          Activity.costForInfant =
            seasonalCost.tariff.find((t) => t.currency === currency)?.costForInfant ||
            Activity.tariff?.find((t) => t.currency === currency)?.costForInfant ||
            0;
        }
      } else {
        if (GROUP_ACTIVITY_CATEGORIES.has(Activity.category)) {
          Activity.costForAdult = Activity.tariff?.find((t) => t.currency === currency)?.costForAdult || 0;
        } else {
          Activity.costForAdult = Activity.tariff?.find((t) => t.currency === currency)?.costForAdult || 0;
          Activity.costForCWB = Activity.tariff?.find((t) => t.currency === currency)?.costForCWB || 0;
          Activity.costForInfant = Activity.tariff?.find((t) => t.currency === currency)?.costForInfant || 0;
        }
      }

      Activity.quantity = 1;
      return Activity;
    });

    onAdd(activitiesToAdd);
    setSelectedActivities([]);
    handleClose();
  };

  const [category, setCategory] = useState("all");
  const [supplier, setSupplier] = useState<string | undefined>();
  const [country, setCountry] = useState(initialCountry || (destinations.length > 0 ? destinations[0] : ""));
  const [city, setCity] = useState<string | undefined>(initialCity);
  const [searchCity, setSearchCity] = useState("");
  const [searchActivity, setSearchActivity] = useState("");
  const [filterPax, setFilterPax] = useState<number | null>(null);
  const [filterVehicleType, setFilterVehicleType] = useState<string | null>(null);
  const debouncedSearchCity = useDebouncedValue(searchCity, 500);
  const debouncedSearchActivity = useDebouncedValue(searchActivity, 500);
  const { message } = AntApp.useApp();
  const convertRates = useInventoryCurrencyConverter(currency);
  const {
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchActivities,
    items: activitiesList,
    data: activityPages,
    isError,
    error,
  } = useInfiniteActivitiesQuery(
    {
      country,
      city,
      search: debouncedSearchActivity,
      category: category === "all" ? undefined : category,
      supplierId: supplier,
      vehicleType: filterVehicleType || undefined,
      minPax: filterPax,
      activeOnly: true,
      excludeTransportCategories: true,
    },
    open,
  );

  // Get suppliers that support the current destination
  const { data: suppliers } = useListSuppliersQuery(country, true, city);

  useEffect(() => {
    if (isError) {
      console.error(error);
      message.error(error?.message || "Failed to get Activities list");
    }
  }, [isError, error, message]);

  const { isFetching: isFetchingCities, refetch, data: cities } = useGetCitiesQuery(country, debouncedSearchCity);

  useEffect(() => {
    if (country && debouncedSearchCity !== "") {
      refetch();
    }
  }, [country, debouncedSearchCity, refetch]);

  useEffect(() => {
    setCountry(initialCountry || (destinations.length > 0 ? destinations[0] : ""));
    setCity(initialCity);
    if (open) {
      setViewMode("list");
    }
  }, [destinations, initialCity, initialCountry, open]);

  const filteredList = activitiesList.filter((activity) =>
    activities?.every((existing) => existing.id !== activity.id),
  );
  const totalResults = activityPages?.pages[0]?.pagination.total || 0;
  const getActivityRate = (activity: ActivityType): number | null => {
    if (!currency) return null;
    const travelDate = startDate ? dayjs(startDate).add(day, "day") : null;
    const seasonalTariff = travelDate
      ? activity.seasonal?.find((seasonal) => {
          if (seasonal.daterange?.length !== 2) return false;
          const [seasonStart, seasonEnd] = seasonal.daterange;
          return travelDate.isSameOrAfter(dayjs(seasonStart)) && travelDate.isSameOrBefore(dayjs(seasonEnd));
        })?.tariff
      : undefined;
    const tariff =
      seasonalTariff?.find((item) => item.currency === currency) ||
      activity.tariff?.find((item) => item.currency === currency);
    const rate = tariff?.costForAdult ?? activity.cost;

    return typeof rate === "number" && Number.isFinite(rate) && rate > 0 ? rate : null;
  };
  const loadMore = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (hasNextPage && !isFetchingNextPage && target.scrollHeight - target.scrollTop - target.clientHeight < 120) {
      fetchNextPage();
    }
  };

  return (
    <>
      <Modal
        title={
          <div>
            <div className="mb-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-md border border-(--brand-primary)/35 bg-(--brand-primary)/10">
                  <TentTreeIcon className="h-4 w-4 text-(--brand-primary)" />
                </div>
                <div>
                  <h2 className="text-base font-semibold tracking-tight text-[#131B2A]">Add activities</h2>
                  <p className="text-xs text-gray-500">
                    Day {day + 1} inventory{currency ? ` · rates applied in ${currency}` : ""}
                  </p>
                </div>
              </div>
              {canAccessOrg && (
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(true)}
                  className="mr-8 inline-flex h-8 items-center rounded-md border border-[#131B2A] bg-[#131B2A] px-3 text-xs font-semibold text-white transition-colors duration-150 hover:bg-[#263348] active:scale-[0.98]"
                >
                  Create New
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                      Destination
                    </label>
                    <Select
                      className="w-full"
                      size="small"
                      allowClear
                      aria-label="Destination filter"
                      placeholder="All destinations"
                      value={country || undefined}
                      onChange={(value: string | undefined) => {
                        setCountry(value || "");
                        setCity(undefined);
                        onLocationChange?.(value || "", undefined);
                        setSupplier(undefined);
                        setSearchCity("");
                      }}
                      options={
                        destinations === undefined
                          ? []
                          : destinations.map((destination) => ({
                              label: destination,
                              value: destination,
                            }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">City</label>
                    <Select
                      allowClear
                      showSearch
                      size="small"
                      notFoundContent={null}
                      className="w-full"
                      disabled={!country}
                      placeholder={country ? `Cities in ${country}` : "Select destination first"}
                      loading={isFetchingCities}
                      options={
                        searchCity.length === 0
                          ? []
                          : cities?.map((city) => ({
                              label: city.name,
                              value: city.name,
                            }))
                      }
                      value={city}
                      onClear={() => {
                        setCity(undefined);
                        onLocationChange?.(country, undefined);
                      }}
                      onSelect={(value) => {
                        setCity(value);
                        onLocationChange?.(country, value);
                      }}
                      onSearch={setSearchCity}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                  Search inventory
                </label>
                <Input
                  placeholder="Search activities..."
                  size="small"
                  className="w-full"
                  onChange={(e) => setSearchActivity(e.target.value)}
                  allowClear
                  prefix={<Search className="w-4 h-4 text-gray-400" />}
                />
              </div>
            </div>
          </div>
        }
        open={open}
        onOk={() => {
          setSearchCity("");
          handleOk();
        }}
        onCancel={() => {
          setSearchCity("");
          handleClose();
        }}
        footer={
          <div className="flex flex-col items-start justify-between gap-2 border-t border-gray-200 bg-white px-4 py-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="rounded-md border border-(--brand-primary)/40 bg-(--brand-primary)/10 px-2.5 py-1 text-xs font-semibold text-[#8A611C]">
                {selectedActivities.length} selected
              </div>
              {selectedActivities.length > 0 && <span className="text-xs text-gray-500">Activities ready to add</span>}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={handleClose}
                className="h-8 flex-1 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-[#131B2A] transition-colors duration-150 hover:border-gray-400 sm:flex-none"
              >
                Close
              </button>
              <button
                disabled={selectedActivities.length === 0}
                onClick={handleOk}
                className={cx(
                  selectedActivities.length === 0
                    ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                    : "border-[#131B2A] bg-[#131B2A] text-white hover:bg-[#263348]",
                  "h-8 flex-1 rounded-md border px-4 text-center text-xs font-semibold transition-colors duration-150 active:scale-[0.98] sm:flex-none",
                )}
              >
                Add Activities
              </button>
            </div>
          </div>
        }
        classNames={{
          body: "h-[64dvh] !p-0 border-y border-gray-200 overflow-hidden sm:h-[68dvh]",
          header: "!px-4 !py-3 !bg-white",
          content: "!overflow-hidden !rounded-xl !p-0 !shadow-[0_12px_32px_rgba(19,27,42,0.16)]",
          footer: "!m-0",
        }}
        width="95vw"
        style={{
          maxWidth: 1360,
          top: 8,
        }}
        destroyOnClose
      >
        <div className="grid h-full grid-cols-1 grid-rows-[auto_minmax(0,1fr)] bg-white lg:grid-cols-[208px_minmax(0,1fr)] lg:grid-rows-1">
          <aside className="max-h-52 overflow-y-auto border-b border-gray-200 bg-[#FBFBFB] lg:max-h-none lg:border-b-0 lg:border-e">
            <div className="p-3">
              {/* Supplier Filter */}
              <div className="mb-3 lg:mb-4">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white">
                    <Check className="h-3 w-3 text-(--brand-primary)" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Supplier</span>
                </div>
                <Select
                  allowClear
                  size="small"
                  placeholder="Filter by supplier"
                  className="w-full"
                  value={supplier}
                  onClear={() => setSupplier(undefined)}
                  onSelect={(value) => setSupplier(value)}
                  options={suppliers?.map((sup) => ({
                    label: sup.name,
                    value: sup.id,
                  }))}
                  notFoundContent={suppliers?.length === 0 ? "No suppliers found for this destination" : null}
                />
              </div>

              <div className="mb-3 lg:mb-4">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white">
                    <CarFront className="h-3.5 w-3.5 text-(--brand-primary)" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Vehicle</span>
                </div>
                <div className="space-y-3 px-1 lg:px-0">
                  <div className="space-x-2 flex items-center">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">Type</label>
                    <Select
                      disabled={!["Private Transfers", "Private Tours"].includes(category)}
                      allowClear
                      size="small"
                      className="flex-1 mt-1"
                      placeholder="Any vehicle"
                      value={filterVehicleType}
                      onChange={setFilterVehicleType}
                      options={Array.from(
                        new Set(
                          activitiesList
                            ?.filter((a) => a.category === category)
                            .flatMap((a) => a.vehicles?.map((v) => v.vehicleType))
                            .filter(Boolean) || [],
                        ),
                      ).map((type) => ({ label: type as string, value: type as string }))}
                    />
                  </div>
                  <div className="space-x-2 flex items-center">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">Pax</label>
                    <InputNumber
                      disabled={!["Private Transfers", "Private Tours"].includes(category)}
                      size="small"
                      className="flex-1 mt-1"
                      placeholder="e.g. 4"
                      min={1}
                      value={filterPax}
                      onChange={setFilterPax}
                    />
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white">
                  <LayoutGrid className="h-3 w-3 text-(--brand-primary)" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Categories</span>
              </div>
              <div className="grid grid-cols-2 gap-1 lg:grid-cols-1">
                {activityCategories.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setCategory(item.key)}
                    className={cx(
                      category === item.key
                        ? "border-[#131B2A] bg-[#131B2A] text-white"
                        : "border-transparent text-gray-600 hover:border-gray-200 hover:bg-white hover:text-[#131B2A]",
                      "w-full rounded-md border px-2.5 py-1.5 text-left text-xs font-medium transition-colors duration-150",
                    )}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>
          <div className="overflow-y-auto bg-white" onScroll={loadMore}>
            <div className="p-3">
              <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2">
                <p className="text-sm font-semibold text-[#131B2A]">Available activities</p>
                <div className="flex items-center gap-2">
                  <div
                    className="inline-flex rounded-md border border-gray-200 bg-white p-0.5"
                    role="group"
                    aria-label="Activity result view"
                  >
                    <Tooltip title="List View">
                      <button
                        type="button"
                        aria-label="List View"
                        aria-pressed={viewMode === "list"}
                        onClick={() => setViewMode("list")}
                        className={cx(
                          viewMode === "list"
                            ? "bg-[#131B2A] text-white"
                            : "text-gray-500 hover:bg-gray-100 hover:text-[#131B2A]",
                          "flex h-6 w-6 items-center justify-center rounded transition-colors",
                        )}
                      >
                        <List className="h-3.5 w-3.5" />
                      </button>
                    </Tooltip>
                    <Tooltip title="Grid View">
                      <button
                        type="button"
                        aria-label="Grid View"
                        aria-pressed={viewMode === "grid"}
                        onClick={() => setViewMode("grid")}
                        className={cx(
                          viewMode === "grid"
                            ? "bg-[#131B2A] text-white"
                            : "text-gray-500 hover:bg-gray-100 hover:text-[#131B2A]",
                          "flex h-6 w-6 items-center justify-center rounded transition-colors",
                        )}
                      >
                        <Grid2X2 className="h-3.5 w-3.5" />
                      </button>
                    </Tooltip>
                  </div>
                  <span className="text-xs tabular-nums text-gray-500">
                    {isFetching ? "Loading" : `${totalResults} results`}
                  </span>
                </div>
              </div>
              <div
                className={cx(
                  viewMode === "list"
                    ? "space-y-2"
                    : "grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
                )}
              >
                {isLoading &&
                  Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="h-52 animate-pulse rounded-lg border border-gray-200 bg-gray-50" />
                  ))}
                {!isLoading &&
                  (filteredList.length > 0 ? (
                    filteredList.map((activity) => {
                      const isSelected = selectedActivities.some(
                        (selectedActivity) => selectedActivity.id === activity.id,
                      );

                      if (viewMode === "list") {
                        return (
                          <button
                            key={activity.id}
                            className={cx(
                              isSelected
                                ? "border-(--brand-primary) ring-1 ring-inset ring-(--brand-primary)"
                                : "border-gray-200 hover:border-(--brand-primary)/70 hover:shadow-sm",
                              "group relative flex w-full items-start gap-3 rounded-lg border bg-white p-2.5 text-left transition-all duration-150 active:scale-[0.995]",
                            )}
                            onClick={() => toggleSelectedActivity(activity)}
                          >
                            <img
                              src={activity.image || "/images/activity.jpg"}
                              alt=""
                              className="h-20 w-24 shrink-0 rounded-md object-cover"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-start justify-between gap-3">
                                <h3 className="text-sm font-semibold leading-snug text-[#131B2A]">{activity.name}</h3>
                                {isSelected && (
                                  <Check className="h-4 w-4 shrink-0 text-(--brand-primary)" aria-label="Selected" />
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                                <span className="inline-flex items-center">
                                  <MapPin className="mr-1 h-3 w-3" />
                                  {activity.city || `Across ${activity.country}`}
                                </span>
                                <span className="rounded border border-gray-200 bg-[#FBFBFB] px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                                  {activity.category}
                                </span>
                                {activity.supplier && <span className="text-[11px]">{activity.supplier.name}</span>}
                              </div>
                            </div>
                            {currency && (
                              <div className="shrink-0 text-right">
                                <span className="block text-[10px] font-medium text-gray-400">From</span>
                                <span className="text-xs font-semibold tabular-nums text-[#131B2A]">
                                  {(() => {
                                    const rate = getActivityRate(activity);
                                    return rate === null ? "Rate unavailable" : `${currency} ${rate.toLocaleString()}`;
                                  })()}
                                </span>
                              </div>
                            )}
                          </button>
                        );
                      }

                      return (
                        <button
                          key={activity.id}
                          className={cx(
                            isSelected
                              ? "border-(--brand-primary) ring-1 ring-inset ring-(--brand-primary)"
                              : "border-gray-200 hover:border-(--brand-primary)/70 hover:shadow-sm",
                            "group relative flex flex-col overflow-hidden rounded-lg border bg-white text-start transition-all duration-150 active:scale-[0.99]",
                          )}
                          onClick={() => toggleSelectedActivity(activity)}
                        >
                          <div className="relative overflow-hidden">
                            <img
                              src={activity.image || "/images/activity.jpg"}
                              alt="Activity"
                              className="h-24 w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                            />

                            {isSelected && (
                              <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-(--brand-primary) bg-(--brand-primary) text-[#131B2A] shadow-sm">
                                <Check className="h-4 w-4" />
                              </div>
                            )}

                            <div className="absolute bottom-2 left-2">
                              <div className="rounded-md border border-white/70 bg-white px-2 py-1 text-[10px] font-semibold text-[#131B2A] shadow-sm">
                                {activity.category}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-1 flex-col p-2.5">
                            <h3
                              title={activity.name}
                              className="mb-1.5 line-clamp-2 text-sm font-semibold leading-snug text-[#131B2A]"
                            >
                              {activity.name}
                            </h3>

                            <div className="mb-1.5 flex items-center text-xs text-gray-500">
                              <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{activity.city || `Across ${activity.country}`}</span>
                            </div>

                            {activity.supplier && (
                              <div className="mb-1.5">
                                <div className="inline-flex max-w-full items-center rounded border border-gray-200 bg-[#FBFBFB] px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                                  <Check className="w-3 h-3 mr-1" />
                                  <span className="truncate">{activity.supplier.name}</span>
                                </div>
                              </div>
                            )}

                            {currency && (
                              <div className="mt-auto flex min-h-7 items-baseline justify-between gap-2 border-t border-gray-100 pt-1.5">
                                <span className="text-[10px] font-medium text-gray-400">From</span>
                                {(() => {
                                  const activityCost = getActivityRate(activity);
                                  if (activityCost === null) {
                                    return (
                                      <span className="text-[11px] font-medium text-gray-400">Rate unavailable</span>
                                    );
                                  }

                                  return (
                                    <span className="truncate text-right text-xs font-semibold tabular-nums text-[#131B2A]">
                                      {currency} {activityCost.toLocaleString()}
                                      <span className="ml-1 font-normal text-gray-400">/person</span>
                                    </span>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="col-span-full text-center py-8 sm:py-12 lg:py-16">
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-[#FBFBFB]">
                        <TentTreeIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No activities found</h3>
                      <p className="text-gray-500 text-sm max-w-sm mx-auto">
                        Try adjusting your filters or search terms to find more activities
                      </p>
                    </div>
                  ))}
                {isFetchingNextPage &&
                  Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`more-${index}`}
                      className="h-52 animate-pulse rounded-lg border border-gray-200 bg-gray-50"
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {canAccessOrg && (
        <Modal
          title={
            <div className="flex items-center gap-3 pb-1">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-(--brand-primary)/35 bg-(--brand-primary)/10">
                <TentTreeIcon className="h-4 w-4 text-(--brand-primary)" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight text-[#131B2A]">Create activity inventory</h2>
              </div>
            </div>
          }
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          footer={null}
          width="90vw"
          style={{ maxWidth: 1000, top: 20 }}
          destroyOnClose
        >
          <EditActivityRepositoryPage
            isModal
            onSuccess={(newActivity) => {
              setIsCreateModalOpen(false);
              refetchActivities().then(() => {
                setSelectedActivities((prev) => {
                  if (prev.some((a) => a.id === newActivity.id)) return prev;
                  return [...prev, newActivity];
                });
              });
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </Modal>
      )}
    </>
  );
};
